import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  setDoc,
  getDoc,
  Timestamp,
} from "firebase/firestore";
import { db, loginAdmin } from "../../Firebase";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import { CircleLoader } from "react-spinners";

// ─── Custom styles for react-select (dark-themed label, white dropdown) ───────
const selectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: "8px",
    border: "none",
    outline: "none",
    boxShadow: "none",
    fontSize: "14px",
    fontFamily: "inherit",
    cursor: "pointer",
    minHeight: "44px",
  }),
  menu: (base) => ({ ...base, zIndex: 9999, fontSize: "14px", fontFamily: "inherit" }),
  option: (base, { isFocused }) => ({
    ...base,
    backgroundColor: isFocused ? "#f3f4f6" : "white",
    color: "#1f2937",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  }),
  singleValue: (base) => ({ ...base, color: "#1f2937", fontSize: "14px" }),
  placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: "14px" }),
};

// Custom option with avatar
const UserOption = ({ innerProps, data, isFocused }) => (
  <div
    {...innerProps}
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "8px 12px",
      backgroundColor: isFocused ? "#f3f4f6" : "white",
      cursor: "pointer",
    }}
  >
    <Avatar
      src={data.imageUrl || ""}
      alt={data.label}
      sx={{ width: 32, height: 32, flexShrink: 0 }}
    />
    <div>
      <div style={{ fontWeight: 600, fontSize: "14px", color: "#1f2937" }}>{data.label}</div>
      {data.email && <div style={{ fontSize: "12px", color: "#6b7280" }}>{data.email}</div>}
    </div>
  </div>
);

// Custom single value with avatar
const UserSingleValue = ({ data }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <Avatar src={data.imageUrl || ""} alt={data.label} sx={{ width: 24, height: 24 }} />
    <span style={{ fontSize: "14px", color: "#1f2937" }}>{data.label}</span>
  </div>
);

// ─── Date helpers ──────────────────────────────────────────────────────────────
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const startOfWeek = (d) => {
  const day = d.getDay(); // 0 = Sun
  const diff = d.getDate() - day;
  return new Date(d.getFullYear(), d.getMonth(), diff);
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

const FILTER_OPTIONS = [
  { key: "all", label: "All" },
  { key: "daily", label: "Today" },
  { key: "weekly", label: "This Week" },
  { key: "monthly", label: "This Month" },
];

const Matches = () => {
  const [users, setUsers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");

  const [user1, setUser1] = useState(null);
  const [user2, setUser2] = useState(null);

  // ── Derived counts ───────────────────────────────────────────────────────────
  const now = new Date();
  const dailyCount = matches.filter((m) => m._matchedAt >= startOfDay(now)).length;
  const weeklyCount = matches.filter((m) => m._matchedAt >= startOfWeek(now)).length;
  const monthlyCount = matches.filter((m) => m._matchedAt >= startOfMonth(now)).length;

  const filteredMatches = matches.filter((m) => {
    if (activeFilter === "daily") return m._matchedAt >= startOfDay(now);
    if (activeFilter === "weekly") return m._matchedAt >= startOfWeek(now);
    if (activeFilter === "monthly") return m._matchedAt >= startOfMonth(now);
    return true;
  });

  // ── Fetch all Vortex users ───────────────────────────────────────────────────
  const fetchUsers = async () => {
    try {
      await loginAdmin();
      const snap = await getDocs(collection(db, "users"));
      const list = snap.docs
        .map((d) => ({
          value: d.id,
          label: d.data().name || "Unnamed",
          email: d.data().email || "",
          imageUrl: d.data().imageUrl || "",
          rawData: d.data(),
        }))
        .filter((u) => !!u.label.trim() && u.label !== "Unnamed");
      setUsers(list);
    } catch (err) {
      console.error("Error fetching users:", err);
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  // ── Fetch all admin-created matches ─────────────────────────────────────────
  const fetchMatches = async () => {
    setLoadingMatches(true);
    try {
      await loginAdmin();
      const snap = await getDocs(collection(db, "matches"));
      const list = snap.docs
        .map((d) => {
          const data = d.data();
          const matchedAt = data.matchedAt?.toDate
            ? data.matchedAt.toDate()
            : new Date();
          return {
            id: d.id,
            _matchedAt: matchedAt,
            matchedAt: matchedAt.toLocaleString(),
            user1Id: data.user1Id || "",
            user2Id: data.user2Id || "",
            user1Name: data.user1Name || "Unknown",
            user2Name: data.user2Name || "Unknown",
            user1Image: data.user1Image || "",
            user2Image: data.user2Image || "",
            status: data.status || "active",
            createdByAdmin: !!data.createdByAdmin,
          };
        })
        .sort((a, b) => b._matchedAt - a._matchedAt);
      setMatches(list);
    } catch (err) {
      console.error("Error fetching matches:", err);
      toast.error("Failed to load matches");
    } finally {
      setLoadingMatches(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchMatches();
  }, []);

  // ── Create a match ───────────────────────────────────────────────────────────
  const handleCreateMatch = async () => {
    if (!user1 || !user2) {
      toast.error("Please select both users");
      return;
    }
    if (user1.value === user2.value) {
      toast.error("Please select two different users");
      return;
    }

    // Check if this match already exists
    const exists = matches.some(
      (m) =>
        (m.user1Id === user1.value && m.user2Id === user2.value) ||
        (m.user1Id === user2.value && m.user2Id === user1.value)
    );
    if (exists) {
      toast.error("A match between these users already exists");
      return;
    }

    setSubmitting(true);
    try {
      await loginAdmin();
      const now = Timestamp.now();

      // 1. Write to top-level `matches` collection
      const matchRef = await addDoc(collection(db, "matches"), {
        user1Id: user1.value,
        user2Id: user2.value,
        user1Name: user1.label,
        user2Name: user2.label,
        user1Image: user1.imageUrl || "",
        user2Image: user2.imageUrl || "",
        matchedAt: now,
        status: "active",
        createdByAdmin: true,
      });

      // 2. Write to user1's matches subcollection
      await setDoc(doc(db, "users", user1.value, "matches", matchRef.id), {
        matchId: matchRef.id,
        uid: user2.value,
        name: user2.label,
        imageUrl: user2.imageUrl || "",
        matchedAt: now,
        status: "active",
      });

      // 3. Write to user2's matches subcollection
      await setDoc(doc(db, "users", user2.value, "matches", matchRef.id), {
        matchId: matchRef.id,
        uid: user1.value,
        name: user1.label,
        imageUrl: user1.imageUrl || "",
        matchedAt: now,
        status: "active",
      });

      toast.success(`✅ Match created: ${user1.label} ↔ ${user2.label}`);
      setUser1(null);
      setUser2(null);
      fetchMatches();
    } catch (err) {
      console.error("Error creating match:", err);
      toast.error("Failed to create match");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Delete a match ───────────────────────────────────────────────────────────
  const handleDeleteMatch = async (match) => {
    if (!window.confirm(`Delete match between ${match.user1Name} and ${match.user2Name}?`)) return;

    setDeletingId(match.id);
    try {
      await loginAdmin();

      // Remove from top-level collection
      await deleteDoc(doc(db, "matches", match.id));

      // Remove from user subcollections (best-effort)
      try { await deleteDoc(doc(db, "users", match.user1Id, "matches", match.id)); } catch (_) {}
      try { await deleteDoc(doc(db, "users", match.user2Id, "matches", match.id)); } catch (_) {}

      toast.success("Match deleted");
      setMatches((prev) => prev.filter((m) => m.id !== match.id));
    } catch (err) {
      console.error("Error deleting match:", err);
      toast.error("Failed to delete match");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{
      flex: 1,
      minHeight: "100vh",
      backgroundColor: "#374151",
      color: "white",
      padding: "24px 16px",
      boxSizing: "border-box",
      overflowX: "hidden",
    }}>
      <ToastContainer />

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "28px", flexWrap: "wrap" }}>
        <h1 style={{ margin: 0, fontSize: "clamp(1.4rem, 4vw, 2.2rem)", fontWeight: 700 }}>
          💘 Create Matches
        </h1>
        <span style={{
          padding: "4px 12px",
          borderRadius: "999px",
          border: "1px solid #4b5563",
          fontSize: "13px",
          color: "#9ca3af",
        }}>
          Testing Tool
        </span>
        <span style={{
          padding: "4px 12px",
          borderRadius: "999px",
          backgroundColor: "#1f2937",
          fontSize: "13px",
          color: "#d1d5db",
        }}>
          {matches.length} total match{matches.length !== 1 ? "es" : ""}
        </span>
      </div>

      {/* ── Stats Cards ────────────────────────────────────────────────────── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: "16px",
        marginBottom: "28px",
        maxWidth: "860px",
      }}>
        {[
          { label: "Today", count: dailyCount, icon: "☀️", color: "#f59e0b", bg: "#1c1a0e" },
          { label: "This Week", count: weeklyCount, icon: "📅", color: "#3b82f6", bg: "#0c1629" },
          { label: "This Month", count: monthlyCount, icon: "🗓️", color: "#ec4899", bg: "#1c0a14" },
          { label: "All Time", count: matches.length, icon: "💘", color: "#10b981", bg: "#061a11" },
        ].map(({ label, count, icon, color, bg }) => (
          <div key={label} style={{
            backgroundColor: "#1f2937",
            borderRadius: "12px",
            padding: "20px",
            borderLeft: `4px solid ${color}`,
          }}>
            <div style={{ fontSize: "22px", marginBottom: "8px" }}>{icon}</div>
            <div style={{ fontSize: "28px", fontWeight: 800, color }}>{count}</div>
            <div style={{ fontSize: "13px", color: "#9ca3af", marginTop: "4px" }}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── Filter Tabs ─────────────────────────────────────────────────────── */}
      <div style={{
        display: "flex",
        gap: "8px",
        marginBottom: "24px",
        flexWrap: "wrap",
      }}>
        {FILTER_OPTIONS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveFilter(key)}
            style={{
              padding: "8px 20px",
              borderRadius: "999px",
              border: activeFilter === key ? "none" : "1px solid #4b5563",
              backgroundColor: activeFilter === key ? "#ec4899" : "transparent",
              color: activeFilter === key ? "white" : "#9ca3af",
              fontSize: "13px",
              fontWeight: activeFilter === key ? 700 : 500,
              cursor: "pointer",
              fontFamily: "inherit",
              transition: "all 0.15s",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Create Match Card ──────────────────────────────────────────────── */}
      <div style={{
        backgroundColor: "#1f2937",
        borderRadius: "12px",
        padding: "24px",
        marginBottom: "32px",
        maxWidth: "860px",
      }}>
        <h2 style={{ margin: "0 0 20px 0", fontSize: "17px", fontWeight: 700, color: "white" }}>
          🔗 Pair Two Users
        </h2>

        {loadingUsers ? (
          <div style={{ display: "flex", alignItems: "center", gap: "12px", color: "#9ca3af" }}>
            <CircleLoader color="#4A90E2" size={24} />
            <span>Loading users…</span>
          </div>
        ) : (
          <>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "20px",
              marginBottom: "24px",
            }}>
              {/* User 1 */}
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={{ fontSize: "13px", color: "#9ca3af", fontWeight: 500 }}>
                  👤 User 1
                </label>
                <Select
                  options={users.filter((u) => u.value !== user2?.value)}
                  value={user1}
                  onChange={setUser1}
                  placeholder="Search user…"
                  styles={selectStyles}
                  components={{ Option: UserOption, SingleValue: UserSingleValue }}
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>

              {/* Heart separator on desktop */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "28px",
                alignSelf: "flex-end",
                paddingBottom: "2px",
              }}>
                ❤️
              </div>

              {/* User 2 */}
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={{ fontSize: "13px", color: "#9ca3af", fontWeight: 500 }}>
                  👤 User 2
                </label>
                <Select
                  options={users.filter((u) => u.value !== user1?.value)}
                  value={user2}
                  onChange={setUser2}
                  placeholder="Search user…"
                  styles={selectStyles}
                  components={{ Option: UserOption, SingleValue: UserSingleValue }}
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            </div>

            {/* Preview */}
            {user1 && user2 && (
              <div style={{
                backgroundColor: "#374151",
                borderRadius: "10px",
                padding: "16px",
                marginBottom: "20px",
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <Avatar src={user1.imageUrl} alt={user1.label} sx={{ width: 44, height: 44 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "14px" }}>{user1.label}</div>
                    <div style={{ fontSize: "12px", color: "#9ca3af" }}>{user1.email}</div>
                  </div>
                </div>
                <div style={{ fontSize: "24px", padding: "0 8px" }}>💞</div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <Avatar src={user2.imageUrl} alt={user2.label} sx={{ width: 44, height: 44 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "14px" }}>{user2.label}</div>
                    <div style={{ fontSize: "12px", color: "#9ca3af" }}>{user2.email}</div>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleCreateMatch}
              disabled={submitting || !user1 || !user2}
              style={{
                padding: "12px 32px",
                backgroundColor: submitting || !user1 || !user2 ? "#4b5563" : "#ec4899",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "15px",
                fontWeight: 700,
                cursor: submitting || !user1 || !user2 ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "background-color 0.2s",
              }}
            >
              {submitting ? (
                <>
                  <CircleLoader color="white" size={16} />
                  Creating…
                </>
              ) : (
                "💘 Create Match"
              )}
            </button>
          </>
        )}
      </div>

      {/* ── Matches Table ──────────────────────────────────────────────────── */}
      <div style={{ maxWidth: "960px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px", flexWrap: "wrap", gap: "8px" }}>
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>
            🗂️ {FILTER_OPTIONS.find((f) => f.key === activeFilter)?.label} Matches
            <span style={{ fontSize: "14px", fontWeight: 400, color: "#9ca3af", marginLeft: "10px" }}>
              {filteredMatches.length} match{filteredMatches.length !== 1 ? "es" : ""}
            </span>
          </h2>
          <button
            onClick={fetchMatches}
            style={{
              padding: "7px 16px",
              borderRadius: "6px",
              border: "1px solid #4b5563",
              backgroundColor: "transparent",
              color: "#d1d5db",
              fontSize: "13px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontFamily: "inherit",
            }}
          >
            {/* Refresh icon */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
              <path d="M21 3v5h-5"/>
            </svg>
            Refresh
          </button>
        </div>

        {loadingMatches ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "180px" }}>
            <CircleLoader color="#4A90E2" size={40} />
          </div>
        ) : filteredMatches.length === 0 ? (
          <div style={{
            backgroundColor: "#1f2937",
            borderRadius: "10px",
            padding: "40px",
            textAlign: "center",
            color: "#6b7280",
            fontSize: "15px",
          }}>
            {matches.length === 0
              ? "No matches yet. Create the first one above ☝️"
              : `No matches found for "${FILTER_OPTIONS.find((f) => f.key === activeFilter)?.label}"`}
          </div>
        ) : (
          <div style={{
            backgroundColor: "#1f2937",
            borderRadius: "12px",
            overflow: "hidden",
          }}>
            {/* Table header */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 48px 1fr 140px 80px 48px",
              gap: "0",
              padding: "12px 16px",
              backgroundColor: "#111827",
              fontSize: "12px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "#6b7280",
              alignItems: "center",
            }}>
              <span>User 1</span>
              <span />
              <span>User 2</span>
              <span>Matched At</span>
              <span>Status</span>
              <span />
            </div>

            {/* Rows */}
            {filteredMatches.map((match) => (
              <div
                key={match.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 48px 1fr 140px 80px 48px",
                  gap: "0",
                  padding: "12px 16px",
                  borderTop: "1px solid #374151",
                  alignItems: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#253046"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
              >
                {/* User 1 */}
                <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                  <Avatar
                    src={match.user1Image}
                    alt={match.user1Name}
                    sx={{ width: 36, height: 36, flexShrink: 0 }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {match.user1Name}
                    </div>
                    <div style={{ fontSize: "11px", color: "#6b7280", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {match.user1Id.slice(0, 12)}…
                    </div>
                  </div>
                </div>

                {/* Arrow */}
                <div style={{ textAlign: "center", fontSize: "18px" }}>💞</div>

                {/* User 2 */}
                <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                  <Avatar
                    src={match.user2Image}
                    alt={match.user2Name}
                    sx={{ width: 36, height: 36, flexShrink: 0 }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {match.user2Name}
                    </div>
                    <div style={{ fontSize: "11px", color: "#6b7280", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {match.user2Id.slice(0, 12)}…
                    </div>
                  </div>
                </div>

                {/* Matched At */}
                <div style={{ fontSize: "12px", color: "#9ca3af", whiteSpace: "nowrap" }}>
                  {match.matchedAt}
                </div>

                {/* Status */}
                <div>
                  <Chip
                    label={match.status}
                    size="small"
                    sx={{
                      backgroundColor: match.status === "active" ? "#064e3b" : "#374151",
                      color: match.status === "active" ? "#6ee7b7" : "#9ca3af",
                      fontSize: "11px",
                      fontWeight: 700,
                      textTransform: "capitalize",
                    }}
                  />
                  {match.createdByAdmin && (
                    <div style={{ fontSize: "10px", color: "#6b7280", marginTop: "2px" }}>
                      admin test
                    </div>
                  )}
                </div>

                {/* Delete */}
                <button
                  onClick={() => handleDeleteMatch(match)}
                  disabled={deletingId === match.id}
                  title="Delete match"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: deletingId === match.id ? "not-allowed" : "pointer",
                    color: "#ef4444",
                    opacity: deletingId === match.id ? 0.4 : 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px",
                    borderRadius: "4px",
                  }}
                >
                  {deletingId === match.id ? (
                    <CircleLoader color="#ef4444" size={16} />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path fillRule="evenodd" d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 013.369 0c1.603.051 2.815 1.387 2.815 2.951zm-6.136-1.452a51.196 51.196 0 013.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 00-6 0v-.113c0-.794.609-1.428 1.364-1.452zm-.355 5.945a.75.75 0 10-1.5.058l.347 9a.75.75 0 101.499-.058l-.346-9zm5.48.058a.75.75 0 10-1.498-.058l-.347 9a.75.75 0 001.5.058l.345-9z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Matches;
